import type {DefaultError} from "@tanstack/react-query";
import type {UrlQueryKey} from "~/query/queryClient.ts";
import {queryClient, useCustomQuery} from "~/query/queryClient.ts";
import {queryHttp} from "~/query/queryHttp.ts";
import type {PageResponse} from "~/services/ApiTypes.ts";
import type {ContractListData, ContractSummaryResponse, DatabagContractApi} from "./contractExplorerTypes.ts";
import {toContractRow} from "./contractExplorerTypes.ts";

/** `GET /api/data/{databag_id}/contracts/` */
export const contractsUrl = (bagId: number) => `/data/${bagId}/contracts/`;
/** `GET /api/data/{databag_id}/contracts/summary/` */
export const contractsSummaryUrl = (bagId: number) => `${contractsUrl(bagId)}summary/`;

export type ContractPageParams = {
    bagId: number;
    /** Index of the first row to fetch. */
    offset: number;
    /** Number of rows to fetch (the API caps it at 500). */
    limit: number;
    /** Server-side search over contract title, supplier and file name. */
    search?: string;
    /** Lifecycle filter (`draft` / `signed` / `active` / `expiring` / `expired`). */
    lifecycle?: string;
    /** Cleaned contract type filter (value from the summary's `type_counts`). */
    contractType?: string;
    /** DRF ordering: `contracted_amount`, `-contracted_amount`, `contract_title`, … (see `ORDERING_KEYS`). */
    ordering?: string;
    /** Reference date the backend calculates `lifecycle` against (e.g. the end-of-data day). */
    asOf?: string;
};

function requestParams(p: ContractPageParams): Record<string, string | number> {
    const params: Record<string, string | number> = {offset: p.offset, limit: p.limit};
    if (p.search) params.search = p.search;
    if (p.lifecycle) params.lifecycle = p.lifecycle;
    if (p.contractType) params.contract_type = p.contractType;
    if (p.ordering) params.ordering = p.ordering;
    if (p.asOf) params.as_of = p.asOf;
    return params;
}

const contractPageQueryKey = (p: ContractPageParams): UrlQueryKey =>
    [contractsUrl(p.bagId), {params: requestParams(p)}];

/**
 * One window of the contract list (DRF LimitOffset envelope).
 *
 * Goes through the query client (not the axios instance directly) so windows
 * stay cached and de-duplicated by TanStack — the grid's own data-source cache
 * is switched off because its key does not include our search/status params.
 */
export async function fetchContractPage(p: ContractPageParams): Promise<ContractListData> {
    const data = await queryClient.fetchQuery<PageResponse<DatabagContractApi>>({
        queryKey: contractPageQueryKey(p),
        staleTime: 30_000,
    });
    return {
        rows: (data.results ?? []).map(toContractRow),
        total: data.count ?? 0,
    };
}

/**
 * The whole-databag figures (lifecycle counts, type vocabulary, reference
 * date, currency). They do not change with search, filters or paging, so this
 * is fetched once per databag and kept for a long time.
 *
 * Keyed under `contractsUrl(bagId)` so the review mutation's prefix
 * invalidation refreshes it along with the list windows.
 */
export function useContractSummaryQuery(bagId: number, asOf?: string) {
    const queryKey: UrlQueryKey = asOf
        ? [contractsSummaryUrl(bagId), {params: {as_of: asOf}}]
        : [contractsSummaryUrl(bagId)];
    return useCustomQuery<ContractSummaryResponse, DefaultError, ContractSummaryResponse, UrlQueryKey>({
        queryKey,
        enabled: bagId > 0,
        staleTime: 5 * 60_000,
    });
}

/** Mints the download link for one contract's source document. */
export async function fetchContractDownloadUrl(bagId: number, contractId: number): Promise<string> {
    const {data} = await queryHttp.get<{download_url: string}>(
        `${contractsUrl(bagId)}${contractId}/download-url/`,
    );
    return data.download_url;
}
